import { useCallback, useMemo, useState } from "react";
import { api, RentApiError } from "@/components/rent/core/api";
import type { Split } from "./SplitPaymentEditor";
import { computeLateFeePaise } from "./late-fee";

export interface LateFeeConfig {
  perDayPaise: number | null;
  graceDays: number;
  capPaise: number | null;
}

/** Snapshot of an already-recorded (PAID/PARTIAL) payment used to prefill edit mode. */
export interface InitialPayment {
  paidDate: string;
  splits: Split[];
  lateFeePaise: number;
  notes: string;
}

export function useRecordPayment(opts: {
  mode?: "create" | "edit";
  paymentId: string;
  dueAmountPaise: number;
  dueDate: string;
  lateFeeConfig?: LateFeeConfig | null;
  initialPayment?: InitialPayment;
  onSuccess?: (paymentId: string) => void;
}) {
  const mode = opts.mode ?? "create";
  const today = new Date().toISOString().slice(0, 10);
  const [paidDate, setPaidDate] = useState(opts.initialPayment?.paidDate ?? today);
  const [splits, setSplits] = useState<Split[]>(
    opts.initialPayment?.splits ?? [{ amount_paise: opts.dueAmountPaise, payment_mode: "UPI" }],
  );
  const [lateFeePaise, setLateFeePaise] = useState(opts.initialPayment?.lateFeePaise ?? 0);
  const [notes, setNotes] = useState(opts.initialPayment?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const splitsTotal = splits.reduce((s, r) => s + (r.amount_paise || 0), 0);

  const suggestedLateFeePaise = useMemo(() => {
    if (!opts.lateFeeConfig) return 0;
    const c = opts.lateFeeConfig;
    return computeLateFeePaise(opts.dueDate, paidDate, c.perDayPaise, c.graceDays, c.capPaise);
  }, [opts.dueDate, paidDate, opts.lateFeeConfig]);

  const submit = useCallback(async () => {
    setError(null);
    if (splits.length === 0) {
      setError("at least one split is required");
      return null;
    }
    if (splits.some((s) => !s.amount_paise || s.amount_paise <= 0)) {
      setError("each split needs a positive amount");
      return null;
    }
    setBusy(true);
    try {
      const body = {
        paid_date: paidDate,
        splits,
        late_fee_paise: lateFeePaise,
        notes: notes || undefined,
      };
      const result =
        mode === "edit"
          ? await api.put(`/py-api/v1/payments/${opts.paymentId}`, body)
          : await api.post(`/py-api/v1/payments/${opts.paymentId}/mark-paid`, body);
      opts.onSuccess?.(opts.paymentId);
      return result;
    } catch (e) {
      setError(e instanceof RentApiError ? e.message : "submit failed");
      return null;
    } finally {
      setBusy(false);
    }
  }, [mode, opts.paymentId, paidDate, splits, lateFeePaise, notes, opts.onSuccess]);

  return {
    mode,
    paidDate, setPaidDate,
    splits, setSplits, splitsTotal,
    lateFeePaise, setLateFeePaise, suggestedLateFeePaise,
    notes, setNotes,
    busy, error,
    submit,
  };
}
